/**
 * Height-clearance check for the truck, per route option.
 *
 * The routing request already sends `max_height`, and Mapbox keeps the route
 * off roads whose POSTED limit is lower than the truck. What it will never do
 * is mention the bridge that is merely CLOSE: a 3.8 m underpass over a 3.6 m
 * truck routes as if it were open sky, and the driver finds out from the sign
 * at 60 km/h. This module reads the posted limits along each candidate route
 * from OpenStreetMap (Overpass API) and reports anything near - or, if the two
 * datasets disagree, below - the truck's height, so the sheet can say it
 * before the road does.
 *
 * Honesty rule, same as the rough-road warnings: "checked and found nothing"
 * and "could not check" are different claims. A failed lookup returns null and
 * the UI stays silent; it never renders as all-clear. And a clean scan is
 * only ever "no POSTED limit found" - an unmapped bridge is invisible to both
 * Mapbox and OSM, which is exactly why the margins below are conservative.
 *
 * In OSM the `maxheight` tag lives on the way passing UNDER the structure -
 * the restricted road itself - so a restriction that applies to us lies ON the
 * route line, and a tight `around:` corridor plus a projection filter is
 * enough to reject the parallel frontage road with its own low gate.
 */

import type { LngLat, Route } from '../core/types';
import { cumulativeDistances, nearestPointOnSegment } from '../core/geo';

/** Report anything with less than this much air above the truck. */
export const TIGHT_MARGIN_M = 0.5;
/** Mention (calmly) the lowest limit found when it is within this margin. */
export const NOTE_MARGIN_M = 1.0;
/**
 * How far off the route line a tagged way may sit and still count, metres.
 * Tight on purpose: the restricted way IS the road being driven, and the only
 * offset between it and the route line is two datasets disagreeing about the
 * road's centreline. Wider corridors start swallowing the drive-through and
 * the parking gate on the frontage.
 */
const CORRIDOR_M = 20;
/** Corridor of the Overpass around-filter, metres. */
const AROUND_M = 20;
/** Spacing of the polyline handed to Overpass, metres. */
const SAMPLE_STEP_M = 400;
/** Hard cap on polyline points per route, so the query stays small. */
const MAX_POINTS = 220;

/**
 * Public Overpass servers, tried in order. The main instance load-sheds with
 * 429/504 at busy hours; the mirror usually answers when it does. Plain
 * form-encoded POST with no custom headers, so the browser sends a simple
 * CORS request with no preflight.
 */
const OVERPASS_URLS = [
  'https://overpass-api.de/api/interpreter',
  'https://overpass.kumi.systems/api/interpreter',
  'https://overpass.private.coffee/api/interpreter',
];

/** One posted restriction that matters for this vehicle. */
export interface ClearanceLimit {
  /** Posted clearance, metres. */
  clearanceM: number;
  /** Metres along the route from the start. */
  along: number;
  location: LngLat;
  /**
   * Posted limit strictly below the truck - the two data sources disagree
   * about this road. A limit EQUAL to the truck is legal (the sign permits
   * vehicles up to its figure) and is reported as zero-margin instead.
   */
  blocked: boolean;
  name?: string;
}

export interface RouteClearance {
  /** True when the lookup succeeded - only then may the UI say anything. */
  checked: boolean;
  /** Truck height the scan was run against, metres. */
  vehicleHeightM: number;
  /** Restrictions with less than TIGHT_MARGIN_M to spare, lowest first. */
  limits: ClearanceLimit[];
  /** Lowest posted limit found anywhere on the route, null when none was. */
  lowestM: number | null;
}

/* ------------------------------ OSM values ------------------------------- */

/** Values that mean "no numeric limit signed here". */
const NON_NUMERIC = new Set(['default', 'none', 'unsigned', 'no_sign', 'no_indications', 'below_default']);

/**
 * Parse an OSM maxheight value to metres. Handles "3.5", "3,5", "3.5 m",
 * "12 ft" and the feet-inches form 12'6" - anything else is skipped rather
 * than guessed at.
 */
export function parseMaxheight(raw: string): number | null {
  const s = raw.trim().toLowerCase();
  if (!s || NON_NUMERIC.has(s)) return null;
  const ftIn = s.match(/^(\d+)\s*'\s*(\d+(?:\.\d+)?)?\s*"?$/);
  if (ftIn) return (parseInt(ftIn[1], 10) + (ftIn[2] ? parseFloat(ftIn[2]) : 0) / 12) * 0.3048;
  const ft = s.match(/^(\d+(?:[.,]\d+)?)\s*ft\.?$/);
  if (ft) return parseFloat(ft[1].replace(',', '.')) * 0.3048;
  const m = s.match(/^(\d+(?:[.,]\d+)?)(?:\s*m(?:eters?|etres?)?)?$/);
  if (m) return parseFloat(m[1].replace(',', '.'));
  return null;
}

/* ------------------------------ Overpass --------------------------------- */

interface OverpassElement {
  type: 'node' | 'way';
  id: number;
  lat?: number;
  lon?: number;
  geometry?: Array<{ lat: number; lon: number }>;
  tags?: Record<string, string>;
}

/**
 * Thin the route line to a polyline Overpass will accept: one point roughly
 * every SAMPLE_STEP_M, endpoints always included. Formatted lat,lon - the
 * opposite order to GeoJSON, and getting it wrong searches the wrong country.
 */
function overpassPoly(coords: LngLat[], cum: number[]): string {
  const total = cum[cum.length - 1] ?? 0;
  const step = Math.max(SAMPLE_STEP_M, total / MAX_POINTS);
  const parts: string[] = [];
  let next = 0;
  for (let i = 0; i < coords.length; i++) {
    if (cum[i] >= next || i === coords.length - 1) {
      parts.push(`${coords[i][1].toFixed(5)},${coords[i][0].toFixed(5)}`);
      next = cum[i] + step;
    }
  }
  return parts.join(',');
}

/**
 * Every maxheight-tagged way and node near any of the candidate routes, in a
 * single request - one query for the whole sheet is far kinder to the public
 * Overpass server than one per option, and it rate-limits by IP.
 */
async function fetchMaxheightElements(
  polys: string[],
  signal?: AbortSignal,
): Promise<OverpassElement[]> {
  const clauses = polys
    .flatMap((poly) => [
      `way["maxheight"](around:${AROUND_M},${poly});`,
      `way["maxheight:physical"](around:${AROUND_M},${poly});`,
      `node["maxheight"](around:${AROUND_M},${poly});`,
      `node["maxheight:physical"](around:${AROUND_M},${poly});`,
    ])
    .join('\n');
  const query = `[out:json][timeout:25];(\n${clauses}\n);out tags geom;`;

  let lastError: unknown;
  for (const url of OVERPASS_URLS) {
    try {
      const res = await fetch(url, {
        method: 'POST',
        body: new URLSearchParams({ data: query }),
        signal,
      });
      if (!res.ok) throw new Error(`Overpass ${res.status}`);
      const body = (await res.json()) as { elements?: OverpassElement[] };
      return body.elements ?? [];
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
      lastError = err;
    }
  }
  throw lastError instanceof Error ? lastError : new Error('Overpass unreachable');
}

/* ------------------------------ matching --------------------------------- */

function elementPoints(el: OverpassElement): LngLat[] {
  if (el.type === 'node' && el.lat != null && el.lon != null) return [[el.lon, el.lat]];
  return (el.geometry ?? []).map((g) => [g.lon, g.lat] as LngLat);
}

/**
 * Ways a through route would never be driven on. `maxheight` also lives on
 * parking-garage entrances, drive-throughs and private gates BESIDE the road;
 * a 2 m McDonald's canopy 15 m from the carriageway must not read as a low
 * bridge on the route.
 */
const IGNORED_HIGHWAY = new Set([
  'service', 'track', 'path', 'footway', 'cycleway', 'pedestrian', 'steps', 'bridleway', 'corridor',
]);

function isDrivableRestriction(el: OverpassElement): boolean {
  const tags = el.tags ?? {};
  if (el.type === 'way') {
    const hw = tags.highway;
    if (!hw || IGNORED_HIGHWAY.has(hw)) return false;
  }
  if (tags.amenity === 'parking' || tags.parking != null) return false;
  if (['private', 'no', 'customers'].includes(tags.access ?? '')) return false;
  return true;
}

function elementClearance(el: OverpassElement): number | null {
  const tags = el.tags ?? {};
  const values = [tags['maxheight'], tags['maxheight:physical']]
    .filter((v): v is string => typeof v === 'string')
    .map(parseMaxheight)
    .filter((v): v is number => v != null);
  if (!values.length) return null;
  return Math.min(...values);
}

/** Where the element sits relative to the route, or null when it is off it. */
function projectElement(
  points: LngLat[],
  coords: LngLat[],
  cum: number[],
): { along: number; location: LngLat } | null {
  let best: { d: number; along: number; location: LngLat } | null = null;
  for (const p of points) {
    for (let i = 0; i < coords.length - 1; i++) {
      const r = nearestPointOnSegment(p, coords[i], coords[i + 1]);
      if (!best || r.distance < best.d) {
        const segLen = cum[i + 1] - cum[i];
        best = { d: r.distance, along: cum[i] + segLen * r.t, location: p };
      }
    }
  }
  if (!best || best.d > CORRIDOR_M) return null;
  return { along: best.along, location: best.location };
}

function matchToRoute(
  route: Route,
  elements: OverpassElement[],
  heightM: number,
): RouteClearance {
  const coords = route.geometry.coordinates;
  const cum = cumulativeDistances(coords);
  const hits: ClearanceLimit[] = [];
  let lowest: number | null = null;

  for (const el of elements) {
    if (!isDrivableRestriction(el)) continue;
    const clearance = elementClearance(el);
    if (clearance == null) continue;
    const proj = projectElement(elementPoints(el), coords, cum);
    if (!proj) continue;
    if (lowest == null || clearance < lowest) lowest = clearance;
    if (clearance < heightM + TIGHT_MARGIN_M) {
      hits.push({
        clearanceM: clearance,
        along: proj.along,
        location: proj.location,
        blocked: clearance < heightM,
        name: el.tags?.name,
      });
    }
  }

  /*
   * The same structure arrives more than once: the restricted way is often
   * split at the bridge, both carriageways carry the tag, and a node barrier
   * can sit on a tagged way. On the ground it is one bridge - merge anything
   * within 200 m along the route and keep the lowest figure, which is the
   * only one the driver needs.
   */
  hits.sort((a, b) => a.along - b.along);
  const merged: ClearanceLimit[] = [];
  for (const h of hits) {
    const prev = merged[merged.length - 1];
    if (prev && h.along - prev.along < 200) {
      if (h.clearanceM < prev.clearanceM) merged[merged.length - 1] = h;
      continue;
    }
    merged.push(h);
  }
  merged.sort((a, b) => a.clearanceM - b.clearanceM);

  return { checked: true, vehicleHeightM: heightM, limits: merged, lowestM: lowest };
}

/**
 * Posted height limits along each candidate route, index-aligned with the
 * input. One Overpass request covers every option; a failure rejects, and the
 * caller must treat that as "unknown", never as "clear".
 */
export async function scanRouteClearances(
  routes: Route[],
  heightM: number,
  signal?: AbortSignal,
): Promise<RouteClearance[]> {
  const usable = routes.filter((r) => r.geometry.coordinates.length >= 2);
  if (!usable.length) return routes.map(() => ({ checked: true, vehicleHeightM: heightM, limits: [], lowestM: null }));

  const polys = usable.map((r) =>
    overpassPoly(r.geometry.coordinates, cumulativeDistances(r.geometry.coordinates)),
  );
  const elements = await fetchMaxheightElements(polys, signal);

  // Dedupe across routes - shared corridors return the same elements twice.
  const seen = new Set<string>();
  const unique = elements.filter((el) => {
    const key = `${el.type}/${el.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return routes.map((r) => matchToRoute(r, unique, heightM));
}
